import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Calendar } from "lucide-react";


import toast from "react-hot-toast";
import { observer } from "mobx-react-lite";
import { ServiceData } from "../../types/service";
import { useStore } from "../../app/stores/store";
import { KsaBookingFormSchema, ksaBookingSchema } from "../../lib/schemas/ksaBookingSchema";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../components/ui/form";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";

interface BookingKsaFormProps {
  service: ServiceData;
}

export function BookingKsaForm({ service }: BookingKsaFormProps) {
  const navigate = useNavigate();
  const [totalPrice, setTotalPrice] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const {
    bookingStore: { addBooking },
    nationalityStore: {
      loadNationalities,
      NationalitiesDropdown,
    },
  } = useStore();

  const form = useForm<KsaBookingFormSchema>({
    resolver: zodResolver(ksaBookingSchema),
    defaultValues: {
      customerName: "",
      phone: "",
      email: "",
      adultsNumber: 1,
      childrenNumber: 0,
      isGccResident: true,
      serviceId: service?.id || "",
      checkInDate: new Date(),
      checkOutDate: new Date(Date.now() + 24 * 60 * 60 * 1000), // Tomorrow
      checkInTime: "14:00",
      checkOutTime: "12:00",
    },
  });

  const { watch, setValue, handleSubmit, control } = form;

  useEffect(() => {
    loadNationalities();
  }, [loadNationalities]);

  // Set serviceId when service changes
  useEffect(() => {
    if (service?.id) {
      setValue("serviceId", service.id);
    }
  }, [service, setValue]);

  // KSA price calculation - same logic as your original
  useEffect(() => {
    const calculateTotalPrice = () => {
      const adults = watch("adultsNumber") || 0;
      const children = watch("childrenNumber") || 0;
      const isGccResident = watch("isGccResident");

      let price = (adults + children) * Number(service.price);
      if (!isGccResident) {
        price += 100;
      }
      setTotalPrice(price);
      setValue("totalPrice", price);
    };

    calculateTotalPrice();
  }, [
    watch("adultsNumber"),
    watch("childrenNumber"),
    watch("isGccResident"),
    service.price,
    setValue
  ]);

  const onSubmit = async (data: KsaBookingFormSchema) => {
    setIsLoading(true);
    console.log("KSA Form data:", data);

    if (!data.serviceId) {
      toast.error("Please select a service");
      setIsLoading(false);
      return;
    }

    data.totalPrice = totalPrice;

    const result = await addBooking(data);
    setIsLoading(false);

    if (result.status === "success") {
      toast.success("KSA Visa Booking added successfully");
      navigate(`/listings/booking/upload/${result.data}`);
    } else {
      toast.error(`Error: ${result.error}`);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-0">
        {/* Customer Name */}
        <FormField
          control={form.control}
          name="customerName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Customer Name</FormLabel>
              <FormControl>
                <Input 
                id="customerNameInput1"
                  placeholder="Enter your name" 
                  {...field} 
                  className="focus-visible:ring-[#e71875] focus-visible:border-[#e71875] transition-colors duration-200"
                />
              </FormControl>
              <FormMessage className="text-red-500" />
            </FormItem>
          )}
        />

        {/* Email */}
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input 
                  type="email" 
                  placeholder="Enter your email" 
                  {...field}
                  className="focus-visible:ring-[#e71875] focus-visible:border-[#e71875] transition-colors duration-200"
                />
              </FormControl>
              <FormMessage className="text-red-500" />
            </FormItem>
          )}
        />

        {/* Phone */}
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <div className="rounded-md border border-input focus-visible:ring-[#e71875] focus-visible:border-[#e71875] transition-colors duration-200">
                  <PhoneInput
                    international
                    defaultCountry="AE"
                    placeholder="Enter phone number"
                    value={field.value}
                    onChange={field.onChange}
                    className="[&>input]:border-0 [&>input]:ring-0 [&>input]:outline-none [&>input]:focus:ring-0 [&>input]:focus:outline-none flex items-center w-full bg-background px-3 py-2 text-base"
                  />
                </div>
              </FormControl>
              <FormMessage className="text-red-500" />
            </FormItem>
          )}
        />

        {/* Nationality */}
        <FormField
          control={form.control}
          name="nationalityId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nationality</FormLabel>
              <Select
                value={field.value}
                onValueChange={field.onChange}
              >
                <FormControl>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Select nationality" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white z-50">
                  {NationalitiesDropdown?.map((nat) => (
                    <SelectItem key={nat.id} value={nat.id} className="hover:bg-gray-100">
                      {nat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage className="text-red-500" />
            </FormItem>
          )}
        />

        {/* Adults & Children */}
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="adultsNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Adults</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    min={1} 
                    max={9} 
                    {...field}
                    onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                    className="focus-visible:ring-[#e71875] focus-visible:border-[#e71875] transition-colors duration-200"
                  />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="childrenNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Children</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    min={0} 
                    max={9} 
                    {...field}
                    onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    className="focus-visible:ring-[#e71875] focus-visible:border-[#e71875] transition-colors duration-200"
                  />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />
        </div>

        {/* GCC Resident */}
        <FormItem>
          <FormLabel>Are you a GCC Resident?</FormLabel>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={
                watch("isGccResident") === true ? "default" : "outline"
              }
              onClick={() => setValue("isGccResident", true)}
            >
              Yes
            </Button>
            <Button
              type="button"
              size="sm"
              variant={
                watch("isGccResident") === false ? "default" : "outline"
              }
              onClick={() => setValue("isGccResident", false)}
            >
              No
            </Button>
          </div>
        </FormItem>

        {/* Check-in & Check-out Dates */}
       
        {/* Total Price */}
       <div className="mt-4 mb-2 font-bold text-end text-lg">
          Total Price: <span className="text-primary">{totalPrice} AED</span> 
        </div>


        {/* Submit */}
        <Button
          type="submit"
          disabled={isLoading}
          className="w-full bg-primary hover:bg-primary/90 text-lg py-6"
        >
          {isLoading ? "Processing KSA Visa..." : "Book KSA Visa Now"}
        </Button>
      </form>
    </Form>
  );
}

export default observer(BookingKsaForm);